import Card from "common/Boxes/Card";
import { BOT_TYPE_TO_ICON } from "features/settings/common/constants";
import styled, { useTheme } from "styled-components";
import Flex from "common/Boxes/Flex";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import { BOT_TYPE_TO_DISPLAY_NAME } from "@shared/features/bots/constants";
import React from "react";
import {
  BOTS_SUMMARY_CARD_HEIGHT,
  BOTS_SUMMARY_CARD_WIDTH,
  NEW_LOOK_BOTS_SUMMARY_CARD_HEIGHT,
} from "features/dashboard/common/constants";
import { BotSummaryData } from "features/dashboard/common/types";
import BotsCardChart from "features/dashboard/MobileDashboard/Bots/BotsCardChart";
import BotsCardEmpty from "features/dashboard/MobileDashboard/Bots/BotsCardEmpty";
import BotsCardStatistics from "features/dashboard/MobileDashboard/Bots/BotsCardStatistics";
import { BOT_TYPE_TO_BASE_PATH } from "features/bots/common/constants";
import { useHistory } from "react-router-dom";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

import { LanguageNameSpaces } from "@shared/types";

interface DashboardBotsCardProps {
  data: BotSummaryData;
}

const DashboardBotsCard = ({ data }: DashboardBotsCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const { t: tBots } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const history = useHistory();
  const showEmpty = data.numBots === 0;

  const Icon = BOT_TYPE_TO_ICON[data.botType];
  const iconSize = theme.isNewLook ? 20 : 24;
  const icon = (
    <IconBg>
      <Icon
        style={{
          width: iconSize,
          height: iconSize,
          color: theme.colors.primaryBlue,
        }}
      />
    </IconBg>
  );

  const title = (
    <div>
      <Title
        level={3}
        style={theme.isNewLook ? NEW_LOOK_TITLE_STYLE : undefined}
      >
        {t("bots.card_title", {
          botDisplayName: tBots(BOT_TYPE_TO_DISPLAY_NAME[data.botType]),
        })}
      </Title>
      <SecondaryText>
        {t("bots.card_active_summary", {
          activeOutOfTotal:
            data.numBots > 0 ? `${data.numActiveBots}/${data.numBots}` : "0",
        })}
      </SecondaryText>
    </div>
  );

  const onClick = () => history.push(BOT_TYPE_TO_BASE_PATH[data.botType]);

  return (
    <StyledCard
      style={{
        height: theme.isNewLook
          ? NEW_LOOK_BOTS_SUMMARY_CARD_HEIGHT
          : BOTS_SUMMARY_CARD_HEIGHT,
        width: BOTS_SUMMARY_CARD_WIDTH,
      }}
      onClick={onClick}
    >
      <TitleWrapper>
        {icon}
        {title}
      </TitleWrapper>
      {showEmpty ? (
        <BotsCardEmpty botType={data.botType} />
      ) : (
        <>
          <BotsCardStatistics data={data} />
          <BotsCardChart data={data} />
        </>
      )}
    </StyledCard>
  );
};

export default React.memo(DashboardBotsCard);

const NEW_LOOK_TITLE_STYLE = { fontSize: 14.5, lineHeight: "20px" };

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const IconBg = styled.div`
  background: ${(p) => p.theme.colors.lightBlue};
  border-radius: 50px;
  width: 40px;
  height: 40px;
  margin-right: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  ${(p) =>
    p.theme.isNewLook &&
    `
    flex: none;
    width: 36px;
    height: 36px;
    border-radius: 12px;
  `}
`;
const SecondaryText = styled(Text).attrs({ type: "secondary" })`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;
const TitleWrapper = styled(Flex)`
  margin-bottom: 12px;
  justify-content: flex-start;
`;
